import { useMemo } from 'react';

import { useGetOrganizationMembersQuery } from '@features/organizations';
import { PRESCRIBER_ROLES } from '@utils/roleUtils';

export interface ConnectedPrescriber {
  userId: string;
  name: string;
}

/**
 * Org prescribers that are connected to DoseSpot (GDN-1119).
 *
 * A Prescribing Proxy can only prep on behalf of a prescriber who is both
 * licensed (clinician/supervisor role) and registered with DoseSpot. The
 * member list now exposes `dosespot_connected`, so we derive the selectable
 * set here rather than adding a dedicated endpoint.
 */
export function useConnectedPrescribers(options?: { skip?: boolean }) {
  const { data, isLoading, isFetching, error } = useGetOrganizationMembersQuery(
    {},
    { skip: options?.skip },
  );

  const prescribers: ConnectedPrescriber[] = useMemo(() => {
    if (!data?.members) return [];
    return data.members
      .filter(
        m =>
          m.status !== 'inactive' &&
          !!m.dosespot_connected &&
          (m.roles || []).some(r => PRESCRIBER_ROLES.has(r)),
      )
      .map(m => ({ userId: m.user_id, name: m.name || 'Unknown' }));
  }, [data?.members]);

  return { prescribers, isLoading: isLoading || isFetching, error };
}
